import PropTypes from 'prop-types';
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { AppContext } from 'contexts/AppContext';

import { getUser } from 'services/userService';
import { parseDisplayName, parseLegalName } from 'utils';
import { getLocalAuth, updateLocalAuthUser } from 'utils/auth';
import { setUserMetadata } from 'utils/datadog';
import { persistLaunchDarklySnapshot } from 'utils/launchDarklyBootstrap';

import { COOKIE_VERIFICATION_EMAIL_REQUESTED } from 'components/Organisms/BannerIsVerified';
import COMMERCIAL_PROFILE_IMAGE from 'images/avatar-commercial.png';
import RESIDENT_PROFILE_IMAGE from 'images/avatar-resident.png';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { readCookie } from 'utils/cookie';
import logger from 'utils/logger';
import { isDesiredUnitAvailable } from '../services/unitsService';
import useUserProfileQuery, {
  getUserProfileQueryKey,
} from 'hooks/queries/useUserProfileQuery';
import { clearQueue } from 'utils/offlineQueue';

export const UserContext = React.createContext();

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};

const DEFAULT_USER_PROFILE = {
  residentProfileImage: RESIDENT_PROFILE_IMAGE,
  commercialProfileImage: COMMERCIAL_PROFILE_IMAGE,
  applicantId: '',
  customerId: '',
  householdId: '',
  applicationId: '',
  leaseId: '',
  unitId: '',
  portalUserId: '',
  firstName: '',
  lastName: '',
  displayName: '',
  legalName: '',
  phoneNumber: '',
  emailAddress: '',
  birthday: '',
  isProspect: false,
  isApplicant: false,
  isResident: false,
  isGuarantor: false,
  isPriorResident: false,
  isCommercial: false,
  needsRecert: false,
  recertification: {},
  householdType: '',
  leaseReadyForSigning: null,
  areFeesApplied: false,
  areFeesPaid: false,
  prospectPreferences: {},
};

export const UserContextProvider = ({ children }) => {
  const ldClient = useLDClient();
  const queryClient = useQueryClient();
  const { organizationId, propertyId, authenticationPortal } =
    useContext(AppContext);
  const localAuth = getLocalAuth();
  const isVerificationEmailRequested = readCookie(
    COOKIE_VERIFICATION_EMAIL_REQUESTED,
  );
  const {
    marketingInfo: { propertyClass: propertyType },
  } = useContext(AppContext);
  const [authUser, setAuthUser] = useState(localAuth.authUser);
  const [userProfile, setUserProfile] = useState(DEFAULT_USER_PROFILE);
  const [hasUpdate, setHasUpdate] = useState(false);
  const previousUserIdRef = useRef(authUser?.id);
  // Track applicantId/applicationId so we can detect a server-side reset
  // (Fortress reset nullifies the application relationship out-of-band).
  const previousApplicantIdRef = useRef(null);
  const previousApplicationIdRef = useRef(null);
  const isResetHydrationPendingRef = useRef(false);
  const previousProfileUserIdRef = useRef(authUser?.id ?? null);

  const prospectId = authUser?.prospectId?.length ? authUser.prospectId : null;
  const householdProspectId = userProfile?.householdProspectId?.length
    ? userProfile.householdProspectId
    : null;
  const headOfHouseholdProspectId = prospectId ?? householdProspectId;
  const areApplicationFeesPaid = userProfile?.areFeesPaid || false;
  const applicationHouseholdType = userProfile?.householdType;
  const areApplicationFeesUnpaid = !areApplicationFeesPaid;
  const { prospectPreferences } = userProfile;
  const profileQueryKey = useMemo(
    () =>
      getUserProfileQueryKey({
        organizationId,
        propertyId,
        portalUserId: authUser?.id ?? null,
      }),
    [organizationId, propertyId, authUser?.id],
  );

  const toggleHasUpdate = useCallback(() => {
    // Functional update avoids stale closure issues when toggled rapidly.
    setHasUpdate((previousHasUpdate) => !previousHasUpdate);
  }, []);

  const updateAuthUser = useCallback(
    (authUserUpdate) =>
      setAuthUser((prevAuthUser) => ({ ...prevAuthUser, ...authUserUpdate })),
    [],
  );

  useEffect(() => {
    // Wait for propertyType before identify so we don't switch contexts offline
    // and lose bootstrap(localStorage) flags for the cached identify key.
    if (
      !(
        ldClient &&
        authUser?.id &&
        organizationId &&
        propertyId &&
        propertyType
      )
    ) {
      return;
    }

    const context = {
      kind: 'user',
      key: authUser.id,
      organizationId,
      propertyId,
      propertyType,
    };

    const identifyAndPersist = async () => {
      await ldClient.identify(context);

      // Keep a durable snapshot because LD SDK localStorage keys can be removed.
      persistLaunchDarklySnapshot({
        clientSideID: `${process.env.REACT_APP_LAUNCHDARKLY_TOKEN}`,
        context,
        flags: ldClient.allFlags?.(),
      });
    };

    identifyAndPersist();
  }, [authUser?.id, ldClient, organizationId, propertyId, propertyType]);

  useEffect(() => {
    const getPortalUser = async () => {
      const portalUser = await getUser({
        organizationId,
        propertyId,
        id: authUser.id,
      });

      if (portalUser.isVerified !== authUser.isVerified) {
        updateLocalAuthUser({ isVerified: portalUser.isVerified });
        updateAuthUser({ isVerified: portalUser.isVerified });
      }
    };

    if (authenticationPortal && authUser?.id) {
      getPortalUser();
    }
  }, [
    authUser?.id,
    authUser?.isVerified,
    organizationId,
    propertyId,
    isVerificationEmailRequested,
    authenticationPortal,
    updateAuthUser,
  ]);

  // Clear React Query cache when user changes (e.g., new user logs in)
  // This prevents stale data from previous users from affecting the new user's portal experience
  useEffect(() => {
    const currentUserId = authUser?.id;
    const previousUserId = previousUserIdRef.current;

    // If the user ID has changed (and we had a previous user), clear the cache
    if (currentUserId && previousUserId && currentUserId !== previousUserId) {
      queryClient.clear();

      // Reset cross-render reset-detection tracking for the new user.
      // Without this, user B can inherit user A's applicant/application refs
      // and incorrectly trigger reset cleanup.
      previousApplicantIdRef.current = null;
      previousApplicationIdRef.current = null;
      isResetHydrationPendingRef.current = false;
    }

    // Update the ref to track the current user ID
    previousUserIdRef.current = currentUserId;
  }, [authUser?.id, queryClient]);

  // Set user metadata in Datadog when authUser changes
  useEffect(() => {
    if (authUser?.id) {
      setUserMetadata({ sanitizedUser: authUser });
    }
  }, [authUser]);

  const {
    data: profileData,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    refetch: refetchProfile,
  } = useUserProfileQuery();

  // Detect a server-side application reset.
  // Fortress-side reset can clear applicationId while applicantId remains the
  // same record, so we track BOTH transitions and treat either non-empty ->
  // empty change as reset.
  useEffect(() => {
    if (!profileData) return;

    const currentProfileUserId = authUser?.id ?? null;
    const previousProfileUserId = previousProfileUserIdRef.current;
    const currentApplicantId = profileData.applicantId ?? '';
    const currentApplicationId = profileData.applicationId ?? '';

    // Never compare reset transitions across different users.
    // On user switch, profileData can briefly contain the previous user's
    // cached snapshot while the new user's query is loading.
    if (currentProfileUserId !== previousProfileUserId) {
      // Drop all transition baselines when crossing users. Any profile snapshot
      // observed in this frame may still belong to the previous user while the
      // new user's query is hydrating.
      previousApplicantIdRef.current = null;
      previousApplicationIdRef.current = null;
      isResetHydrationPendingRef.current = false;
      previousProfileUserIdRef.current = currentProfileUserId;
      return;
    }

    const previousApplicantId = previousApplicantIdRef.current;
    const previousApplicationId = previousApplicationIdRef.current;

    const didApplicantReset = previousApplicantId && !currentApplicantId;
    const didApplicationReset = previousApplicationId && !currentApplicationId;

    if (didApplicantReset || didApplicationReset) {
      isResetHydrationPendingRef.current = true;

      logger('UserContext', 'Detected server-side reset transition', {
        previousApplicantId,
        currentApplicantId,
        previousApplicationId,
        currentApplicationId,
        didApplicantReset,
        didApplicationReset,
      });

      // Clear application-specific slice of the query cache
      queryClient.removeQueries({ queryKey: ['getPrimaryFormData'] });
      queryClient.removeQueries({ queryKey: ['getSecondaryFormData'] });
      queryClient.removeQueries({ queryKey: ['affordaDetails'] });
      queryClient.removeQueries({
        queryKey: ['getAffordaPortalFormByPortalUserId'],
      });
      queryClient.removeQueries({ queryKey: ['getHouseholdApplicationFees'] });
      queryClient.removeQueries({ queryKey: ['getHouseholdMembers'] });

      // Force the profile to refetch so downstream consumers see the reset
      // state immediately (e.g. applicantId is now empty).
      queryClient.invalidateQueries({ queryKey: profileQueryKey });

      // Remove stale persisted query snapshot and any local persisted portal
      // state so reset always rehydrates from fresh server state.
      localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
      clearQueue();

      const localKeysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key?.startsWith('afforda-portal-') ||
          key?.startsWith('sections-progress-') ||
          key?.startsWith('form-state-')
        ) {
          localKeysToRemove.push(key);
        }
      }
      localKeysToRemove.forEach((key) => localStorage.removeItem(key));

      const sessionKeysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key?.startsWith('afforda-portal-') ||
          key?.startsWith('sections-progress-') ||
          key?.startsWith('form-state-')
        ) {
          sessionKeysToRemove.push(key);
        }
      }
      sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));

      // Keep context aligned with reset state immediately, even before all
      // profile consumers finish refetching.
      setUserProfile((prevUserProfile) => ({
        ...prevUserProfile,
        applicantId: '',
        applicationId: '',
      }));

      logger('UserContext', 'Completed reset-state cleanup', {
        localKeysRemoved: localKeysToRemove.length,
        sessionKeysRemoved: sessionKeysToRemove.length,
      });
    }

    previousApplicantIdRef.current = currentApplicantId;
    previousApplicationIdRef.current = currentApplicationId;
    previousProfileUserIdRef.current = currentProfileUserId;
  }, [profileData, queryClient, profileQueryKey, authUser?.id]);

  useEffect(() => {
    if (hasUpdate) {
      refetchProfile();
    }
  }, [hasUpdate, refetchProfile]);

  useEffect(() => {
    const updateProfile = async () => {
      if (profileData) {
        const customerStatus = profileData?.customerStatus ?? '';
        const customerStatusObj = {
          isProspect: customerStatus === '',
          isApplicant: customerStatus === 'Applicant',
          isResident: customerStatus === 'Current Resident',
          isPriorResident: customerStatus === 'Prior Resident',
        };

        const isPrimaryProspect = profileData?.isPrimaryProspect ?? false;

        const isFinanciallyResponsible =
          profileData?.isFinanciallyResponsible ?? false;

        const householdType = profileData?.householdType ?? '';
        const isUnitAvailable = await isDesiredUnitAvailable(
          organizationId,
          propertyId,
          profileData?.unitId,
        );

        const hasApplicantIdFromProfile = Boolean(profileData?.applicantId);
        const hasApplicationIdFromProfile = Boolean(profileData?.applicationId);
        const requiresApplicantIds =
          customerStatus === 'Applicant' ||
          hasApplicantIdFromProfile ||
          hasApplicationIdFromProfile;
        const hasCompleteApplicantIds =
          hasApplicantIdFromProfile && hasApplicationIdFromProfile;
        const isResetHydration = isResetHydrationPendingRef.current;

        if (
          requiresApplicantIds &&
          !hasCompleteApplicantIds &&
          !isResetHydration
        ) {
          return;
        }

        setUserProfile(() => {
          return {
            id: authUser?.id,
            ...DEFAULT_USER_PROFILE,
            ...profileData,
            applicantId: profileData?.applicantId ?? '',
            applicationId: profileData?.applicationId ?? '',
            householdId: profileData?.householdId ?? '',
            customerId: profileData?.customerId ?? '',
            ...customerStatusObj,
            displayName: parseDisplayName(profileData),
            legalName: parseLegalName(profileData),
            isPrimaryProspect,
            isFinanciallyResponsible,
            householdType,
            isUnitAvailable,
          };
        });

        isResetHydrationPendingRef.current = false;
      }
    };

    updateProfile();
  }, [profileData, authUser?.id, organizationId, propertyId]);

  const userContextValues = useMemo(
    () => ({
      portalUserId: userProfile?.portalUserId ?? null,
      userProfile,
      applicationHouseholdType,
      setUserProfile,
      authUser,
      prospectId: headOfHouseholdProspectId,
      updateAuthUser,
      toggleHasUpdate,
      hasUpdate,
      isLoading: isProfileLoading,
      isRefreshing: isProfileFetching,
      areFeesPaid: userProfile.areFeesPaid,
      areFeesApplied: userProfile.areFeesApplied,
      areApplicationFeesPaid,
      areApplicationFeesUnpaid,
      prospectPreferences,
    }),
    [
      applicationHouseholdType,
      areApplicationFeesPaid,
      areApplicationFeesUnpaid,
      authUser,
      hasUpdate,
      headOfHouseholdProspectId,
      isProfileLoading,
      isProfileFetching,
      prospectPreferences,
      toggleHasUpdate,
      updateAuthUser,
      userProfile,
    ],
  );

  return (
    <UserContext.Provider value={userContextValues}>
      {children}
    </UserContext.Provider>
  );
};

UserContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default { UserContext, UserContextProvider };
